import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { readConsent, setConsent } from '../../lib/sentryConsent';

import { Button } from '@/components/ui/button';
// T1715: besedilo pasice gre skozi i18n — pasica je privolitvena površina
// (GDPR), zato jo mora uporabnik brati v svojem jeziku, ne v slovenščini.
// T2028: izbira ni več samo zapis — `setConsent()` jo takoj uveljavi v Sentryju
// (zapis seje ob napaki in sledenje samo po »Sprejmi vse«).
//
// T2180: pasica je `fixed` na dnu in je pri 1536×820 zakrila zadnji gumb
// zavihka »Formati kode« (»Dodaj format kode«) — do njega se ni dalo
// odrsati. Po vzoru okoljske pasice (`--env-banner-h`, T911) objavi svojo
// višino kot `--cookie-banner-h`; `DashboardLayout` jo doda kot spodnji
// odmik drsnega območja, zato je vsebina vedno dosegljiva nad pasico.
const COOKIE_BANNER_VAR = '--cookie-banner-h';

const CookieBanner = () => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const { isAuthenticated } = useAuthStore();
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const root = document.documentElement;
    const update = () => {
      root.style.setProperty(
        COOKIE_BANNER_VAR,
        isVisible && ref.current ? `${ref.current.offsetHeight}px` : '0px',
      );
    };
    update();
    if (!isVisible) return undefined;
    // Besedilo se na ozkem zaslonu prelomi — višina sledi velikosti okna.
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('resize', update);
      root.style.setProperty(COOKIE_BANNER_VAR, '0px');
    };
  }, [isVisible]);

  useEffect(() => {
    if (!readConsent()) {
      setIsVisible(true);
    }
  }, []);

  const handleAccept = async () => {
    setConsent('accepted');
    setIsVisible(false);

    if (isAuthenticated) {
      try {
        await api.post('auth/gdpr-consent/', { gdpr_consented: true });
      } catch (err) {
        console.error('Napaka pri shranjevanju GDPR privolitve:', err);
      }
    }
  };

  const handleReject = () => {
    setConsent('rejected');
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div ref={ref} className="fixed bottom-0 left-0 right-0 bg-card border-t border-[var(--bp-border)] shadow-lg z-50 p-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="text-body text-[var(--bp-text-2-soft)] text-center sm:text-left">
          {t('cookie_banner.text')} {t('cookie_banner.read_more')}{' '}
          <Link to="/privacy" className="text-[var(--bp-accent-text)] hover:underline font-medium">
            {t('cookie_banner.privacy_link')}
          </Link>.
        </div>
        <div className="flex gap-3 shrink-0">
          <button
            onClick={handleReject}
            className="px-4 py-2 text-body font-medium text-[var(--bp-text-2-soft)] hover:text-[var(--bp-text-1)] border border-[var(--bp-border-faint)] rounded-md hover:bg-[var(--bp-surface-2)] transition-colors"
          >
            {t('cookie_banner.reject')}
          </button>
          <Button
            onClick={handleAccept}
            className="shadow-sm"
          >
            {t('cookie_banner.accept')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
